import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Loader2, Plus, UsersRound } from 'lucide-react';
import { type AttributeDef, numberToPriority, taskToUI, type TaskUI } from '@flexiplanner/types';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';

type NewActivityMode = 'choose' | 'personal' | 'team';

const NEW_ACTIVITY_EVENT = 'flexiplanner:new-activity';

type NewActivityEventDetail = {
  mode?: NewActivityMode;
};

export function openNewActivityFlow(mode: NewActivityMode = 'choose') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<NewActivityEventDetail>(NEW_ACTIVITY_EVENT, { detail: { mode } }));
}

export function NewActivityFlow() {
  const [mode, setMode] = useState<NewActivityMode | null>(null);
  const addTask = useTasksStore(s => s.addTask);
  const { toast } = useToast();

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<NewActivityEventDetail>).detail;
      setMode(detail?.mode || 'choose');
    };

    window.addEventListener(NEW_ACTIVITY_EVENT, handleOpen);
    return () => window.removeEventListener(NEW_ACTIVITY_EVENT, handleOpen);
  }, []);

  const handleCreatePersonalActivity = useCallback(
    async (taskData: Partial<TaskUI>) => {
      try {
        const response = await api.tasks.create({
          title: taskData.title,
          description: taskData.description,
          bundle_id: taskData.bundleId,
          due_date: taskData.deadline,
          duration_minutes: taskData.estimatedDuration,
          priority: taskData.priority ? numberToPriority(taskData.priority) : 'medium',
          state_profile: taskData.stateProfile,
          custom_attributes: taskData.customAttributes || {},
        });
        const createdTask = (response.data as any)?.task || response.data;
        if (response.success && createdTask) {
          addTask(taskToUI(createdTask));
          toast({ title: 'Activity added', description: 'It is ready in your personal planner.' });
          return;
        }
        toast({
          title: 'Activity was not added',
          description: response.error?.message || 'Try again from the New menu.',
          variant: 'destructive',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Try again from the New menu.';
        toast({ title: 'Activity was not added', description: message, variant: 'destructive' });
      }
    },
    [addTask, toast]
  );

  return (
    <>
      <NewActivityChooser
        open={mode === 'choose'}
        onClose={() => setMode(null)}
        onPersonal={() => setMode('personal')}
        onTeam={() => setMode('team')}
      />
      <TaskEditor
        open={mode === 'personal'}
        onClose={() => setMode(null)}
        onSave={handleCreatePersonalActivity}
        task={null}
      />
      <TeamActivityEditor open={mode === 'team'} onClose={() => setMode(null)} />
    </>
  );
}

function NewActivityChooser({
  open,
  onClose,
  onPersonal,
  onTeam,
}: {
  open: boolean;
  onClose: () => void;
  onPersonal: () => void;
  onTeam: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!w-[calc(100vw-1rem)] !max-w-[520px] p-0">
        <DialogHeader className="border-b px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left sm:px-6 sm:pt-6">
          <DialogTitle>New</DialogTitle>
          <DialogDescription>Choose where this activity belongs.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 p-4 sm:p-6">
          <button
            type="button"
            className="group flex min-h-[104px] w-full items-start gap-3 rounded-md border bg-background p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={onPersonal}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Activity className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">Add Activity</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Use your personal bundles, schedule fields, and custom activity fields.
              </span>
            </span>
          </button>
          <button
            type="button"
            className="group flex min-h-[104px] w-full items-start gap-3 rounded-md border bg-background p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={onTeam}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
              <UsersRound className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">Add Team Activity</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Assign shared work with team bundles, assignees, and team custom fields.
              </span>
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type TeamActivityDraft = {
  title: string;
  description: string;
  assigned_to_user_id: string;
  bundle_id: string;
  due_date: string;
  duration_minutes: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee_decides_duration: boolean;
};

const initialTeamActivityDraft: TeamActivityDraft = {
  title: '',
  description: '',
  assigned_to_user_id: '',
  bundle_id: '',
  due_date: '',
  duration_minutes: '45',
  priority: 'medium',
  assignee_decides_duration: false,
};

function TeamActivityEditor({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [workspace, setWorkspace] = useState<any | null>(null);
  const [draft, setDraft] = useState<TeamActivityDraft>(initialTeamActivityDraft);
  const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const addTask = useTasksStore(s => s.addTask);
  const currentUserId = useUserStore(s => s.userId);
  const { toast } = useToast();

  const activeMembers = useMemo(
    () => (workspace?.members || []).filter((member: any) => member.status === 'active' && member.user_id),
    [workspace]
  );
  const teamAttributes = useMemo<AttributeDef[]>(
    () => (Array.isArray(workspace?.attributes) ? workspace.attributes : []),
    [workspace]
  );
  const activeBundles = useMemo(
    () => (workspace?.bundles || []).filter((bundle: any) => bundle.status !== 'archived'),
    [workspace]
  );

  useEffect(() => {
    if (!open) return;
    setStatus('');
    void loadTeams();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStatus('');
      setDraft(initialTeamActivityDraft);
      setAttributeValues({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !selectedTeamId) return;
    setAttributeValues({});
    void loadWorkspace(selectedTeamId);
  }, [open, selectedTeamId]);

  useEffect(() => {
    if (!workspace) return;
    setDraft(current => {
      const existingAssigneeIsValid = activeMembers.some(
        (member: any) => member.user_id === current.assigned_to_user_id
      );
      const existingBundleIsValid = activeBundles.some((bundle: any) => bundle.id === current.bundle_id);
      const nextAssignee =
        existingAssigneeIsValid
          ? current.assigned_to_user_id
          : activeMembers.find((member: any) => member.user_id === currentUserId)?.user_id ||
            activeMembers[0]?.user_id ||
            '';
      const nextBundle = existingBundleIsValid
        ? current.bundle_id
        : activeBundles[0]?.id || '';
      return {
        ...current,
        assigned_to_user_id: nextAssignee,
        bundle_id: nextBundle,
      };
    });
  }, [workspace, activeMembers, activeBundles, currentUserId]);

  const defaultAssigneeId = useMemo(
    () =>
      activeMembers.find((member: any) => member.user_id === currentUserId)?.user_id ||
      activeMembers[0]?.user_id ||
      '',
    [activeMembers, currentUserId]
  );
  const defaultBundleId = activeBundles[0]?.id || '';

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await api.teams.list();
      if (!response.success) {
        setStatus(response.error?.message || 'Could not load team workspaces.');
        return;
      }
      const nextTeams = response.data || [];
      setTeams(nextTeams);
      setSelectedTeamId(current =>
        current && nextTeams.some((team: any) => team.id === current)
          ? current
          : nextTeams[0]?.id || ''
      );
      if (nextTeams.length === 0) setWorkspace(null);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not load team workspaces.');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadWorkspace = async (teamId: string) => {
    setLoadingWorkspace(true);
    setWorkspace(null);
    try {
      const response = await api.teams.get(teamId);
      if (!response.success) {
        setStatus(response.error?.message || 'Could not load the selected team workspace.');
        return;
      }
      setWorkspace(response.data);
      setStatus('');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not load the selected team workspace.');
    } finally {
      setLoadingWorkspace(false);
    }
  };

  const resetDraft = () => {
    setDraft(current => ({
      ...initialTeamActivityDraft,
      assigned_to_user_id: current.assigned_to_user_id || defaultAssigneeId,
      bundle_id: current.bundle_id || defaultBundleId,
    }));
    setAttributeValues({});
  };

  const createTeamActivity = async () => {
    if (!selectedTeamId) return setStatus('Choose a team workspace.');
    if (!draft.title.trim()) return setStatus('Activity title is required.');
    if (!draft.bundle_id) return setStatus('Choose a team bundle before assigning an activity.');
    if (!draft.assigned_to_user_id) return setStatus('Choose an assignee.');

    const missingTeamFields = teamAttributes
      .filter(attribute => Boolean(attribute.required))
      .filter(attribute => !String(attributeValues[attribute.attr_id] ?? '').trim())
      .map(attribute => attribute.name);
    if (missingTeamFields.length > 0) {
      return setStatus(
        `Fill required team field${missingTeamFields.length === 1 ? '' : 's'}: ${missingTeamFields.join(', ')}.`
      );
    }

    setSaving(true);
    setStatus('');
    try {
      const customAttributes = buildTeamActivityAttributes(teamAttributes, attributeValues);
      const response = await api.teams.createActivity(selectedTeamId, {
        ...draft,
        duration_minutes: draft.assignee_decides_duration ? undefined : Number(draft.duration_minutes),
        due_date: draft.due_date ? new Date(draft.due_date).toISOString() : '',
        created_from: 'new_activity_flow',
        custom_attributes: {
          ...customAttributes,
          ...(draft.assignee_decides_duration ? { assignee_decides_duration: true } : {}),
        },
      });
      if (!response.success) {
        setStatus(response.error?.message || 'Could not create team activity.');
        return;
      }
      const createdTask = (response.data as any)?.task || response.data;
      if (createdTask?.user_id && createdTask.user_id === currentUserId) {
        addTask(taskToUI(createdTask));
      }
      resetDraft();
      await loadWorkspace(selectedTeamId);
      toast({ title: 'Team activity assigned', description: 'It was added to the assignee planner.' });
      onClose();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not create team activity.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTeam = teams.find(team => team.id === selectedTeamId);
  const readyForAssignment = Boolean(selectedTeamId && activeMembers.length > 0 && activeBundles.length > 0);

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none !translate-y-[-50%] flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[760px] sm:rounded-lg sm:border sm:p-6">
        <DialogHeader className="shrink-0 border-b bg-background/96 px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left backdrop-blur sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
          <DialogTitle>Add Team Activity</DialogTitle>
          <DialogDescription>
            Assign shared work with the selected team workspace fields.
          </DialogDescription>
        </DialogHeader>

        <div className="native-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
          <div className="grid gap-4 py-4">
            {status && (
              <div className="rounded-md border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                {status}
              </div>
            )}

            {loadingTeams ? (
              <div className="flex min-h-32 items-center justify-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading team workspaces
              </div>
            ) : teams.length === 0 ? (
              <div className="rounded-md border bg-muted/20 p-4 text-sm">
                <p className="font-medium">No team workspace found.</p>
                <p className="mt-1 text-muted-foreground">
                  Create or join a team before assigning shared activities.
                </p>
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to="/teams" onClick={onClose}>
                    Open Teams
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-4 rounded-md border bg-muted/20 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                  <div className="grid gap-2">
                    <Label>Team workspace</Label>
                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Badge variant="outline" className="w-fit rounded-md">
                    {selectedTeam?.role === 'manager' ? 'Administrator' : 'Member'}
                  </Badge>
                </div>

                {loadingWorkspace ? (
                  <div className="flex min-h-32 items-center justify-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading team fields
                  </div>
                ) : (
                  <>
                    {!readyForAssignment && (
                      <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                        {activeMembers.length === 0
                          ? 'Add an active team member before assigning activities.'
                          : 'Create a team bundle before assigning activities.'}
                        <Button asChild variant="outline" size="sm" className="mt-3 w-full sm:w-auto">
                          <Link
                            to={selectedTeamId ? `/teams/${encodeURIComponent(selectedTeamId)}/projects-bundles` : '/teams'}
                            onClick={onClose}
                          >
                            Open team workspace
                          </Link>
                        </Button>
                      </div>
                    )}

                    <div className="grid gap-2">
                      <Label htmlFor="newTeamActivityTitle">Title</Label>
                      <Input
                        id="newTeamActivityTitle"
                        value={draft.title}
                        onChange={event => setDraft({ ...draft, title: event.target.value })}
                        placeholder="Activity title"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="newTeamActivityDescription">Notes or source context</Label>
                      <Textarea
                        id="newTeamActivityDescription"
                        className="min-h-24"
                        value={draft.description}
                        onChange={event => setDraft({ ...draft, description: event.target.value })}
                        placeholder="Notes or source context"
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Assignee</Label>
                        <Select
                          value={draft.assigned_to_user_id}
                          onValueChange={assigned_to_user_id => setDraft({ ...draft, assigned_to_user_id })}
                          disabled={activeMembers.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Assignee" />
                          </SelectTrigger>
                          <SelectContent>
                            {activeMembers.map((member: any) => (
                              <SelectItem key={member.id} value={member.user_id}>
                                {member.display_name || member.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Bundle</Label>
                        <Select
                          value={draft.bundle_id}
                          onValueChange={bundle_id => setDraft({ ...draft, bundle_id })}
                          disabled={activeBundles.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Bundle" />
                          </SelectTrigger>
                          <SelectContent>
                            {activeBundles.map((bundle: any) => (
                              <SelectItem key={bundle.id} value={bundle.id}>
                                {bundle.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="newTeamActivityDue">Due date</Label>
                        <Input
                          id="newTeamActivityDue"
                          type="datetime-local"
                          value={draft.due_date}
                          onChange={event => setDraft({ ...draft, due_date: event.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="newTeamActivityDuration">Duration minutes</Label>
                        <Input
                          id="newTeamActivityDuration"
                          type="number"
                          min="1"
                          value={draft.duration_minutes}
                          disabled={draft.assignee_decides_duration}
                          onChange={event => setDraft({ ...draft, duration_minutes: event.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Priority</Label>
                        <Select
                          value={draft.priority}
                          onValueChange={(priority: TeamActivityDraft['priority']) => setDraft({ ...draft, priority })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {teamAttributes.length > 0 && (
                      <div className="grid gap-3 rounded-md border bg-muted/20 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-medium">Team custom fields</h3>
                            <p className="text-xs text-muted-foreground">
                              These values stay with the team activity.
                            </p>
                          </div>
                          <Badge variant="outline" className="rounded-md">
                            {teamAttributes.filter(attribute => Boolean(attribute.required)).length} required
                          </Badge>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {teamAttributes.map(attribute => (
                            <TeamAttributeInput
                              key={attribute.attr_id}
                              attribute={attribute}
                              value={attributeValues[attribute.attr_id] || ''}
                              onChange={value =>
                                setAttributeValues(current => ({
                                  ...current,
                                  [attribute.attr_id]: value,
                                }))
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <label className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm">
                      <Checkbox
                        checked={draft.assignee_decides_duration}
                        onCheckedChange={checked =>
                          setDraft({ ...draft, assignee_decides_duration: Boolean(checked) })
                        }
                      />
                      <span>
                        <span className="block font-medium">Let assignee decide duration</span>
                        <span className="block text-xs text-muted-foreground">
                          Fluxiplanner can infer a starter estimate and prompt the assignee before scheduling.
                        </span>
                      </span>
                    </label>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t bg-background/96 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:mt-6 sm:border-0 sm:bg-transparent sm:p-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={createTeamActivity}
            disabled={saving || loadingTeams || loadingWorkspace || teams.length === 0 || !readyForAssignment}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Add Team Activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamAttributeInput({
  attribute,
  value,
  onChange,
}: {
  attribute: AttributeDef;
  value: string;
  onChange: (value: string) => void;
}) {
  const label = `${attribute.name}${attribute.required ? ' *' : ''}`;
  const options = parseAttributeOptions(attribute.options);
  if (attribute.data_type === 'enum') {
    return (
      <div className="grid gap-2">
        <Label>{label}</Label>
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${attribute.name}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (attribute.data_type === 'boolean') {
    return (
      <label className="flex min-h-10 items-center gap-3 rounded-md border bg-background px-3 py-2 text-sm">
        <Checkbox checked={value === 'true'} onCheckedChange={checked => onChange(Boolean(checked) ? 'true' : 'false')} />
        <span className="min-w-0 truncate">{label}</span>
      </label>
    );
  }

  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        type={attributeInputType(attribute)}
        value={value}
        onChange={event => onChange(event.target.value)}
        min={attribute.min_val ?? undefined}
        max={attribute.max_val ?? undefined}
      />
    </div>
  );
}

function attributeInputType(attribute: AttributeDef): 'number' | 'date' | 'text' {
  if (['float', 'integer', 'Number'].includes(attribute.data_type)) return 'number';
  if (attribute.data_type === 'date') return 'date';
  return 'text';
}

function buildTeamActivityAttributes(attributes: AttributeDef[] = [], values: Record<string, string>) {
  const output: Record<string, unknown> = {};
  for (const attribute of attributes) {
    const raw = values[attribute.attr_id];
    if (raw === undefined || raw === '') continue;
    const value = coerceAttributeValue(attribute, raw);
    output[attribute.attr_id] = value;
    output[normalizeAttributeKey(attribute.name)] = value;
  }
  return output;
}

function coerceAttributeValue(attribute: AttributeDef, value: string): unknown {
  if (['float', 'integer', 'Number'].includes(attribute.data_type)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : value;
  }
  if (attribute.data_type === 'boolean') return value === 'true';
  return value;
}

function normalizeAttributeKey(name: unknown): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function parseAttributeOptions(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(option => String(option).trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(option => String(option).trim()).filter(Boolean);
  } catch {
    // Fall through to delimited parsing.
  }
  return value
    .split(/[\n,]/)
    .map(option => option.trim())
    .filter(Boolean);
}
